import type { DropdownMenuItem } from '@nuxt/ui';
import type { ComponentPublicInstance, Ref } from 'vue';
import type { TabsItem } from '#ui/types';
import { computed, nextTick, watch } from 'vue';
import { get } from '#layers/base/utils/object';
import { useOverflow } from './use-overflow';

interface UseCollapsibleTabsOptions<T extends TabsItem> {
	tabsRef: Ref<ComponentPublicInstance | null>;
	overflowTriggerRef: Ref<HTMLElement | null>;
	items: () => T[] | undefined;
	modelValue: () => string | number | undefined;
	labelKey: () => string;
	enabled: () => boolean;
	onSelect: (value: string | number) => void;
}

function getAvailableFlexWidth(el: HTMLElement): number {
	const parent = el.parentElement;
	if (!parent) return el.offsetWidth;
	const gap = Number.parseFloat(getComputedStyle(parent).columnGap) || 0;
	let siblingWidth = 0;

	for (const child of parent.children) {
		if (child !== el) siblingWidth += (child as HTMLElement).offsetWidth;
	}

	return Math.max(0, parent.offsetWidth - siblingWidth - (parent.children.length - 1) * gap);
}

/** Manages overflow state for DTabs — hides items that don't fit and provides dropdown menu items for the hidden ones. */
export function useCollapsibleTabs<T extends TabsItem>(options: UseCollapsibleTabsOptions<T>) {
	const rootEl = computed<HTMLElement | null>(() => options.tabsRef.value?.$el ?? null);
	const listEl = computed<HTMLElement | null>(() => rootEl.value?.querySelector('[data-slot="list"]') ?? null);
	const triggerEl = computed<HTMLElement | null>(() => options.overflowTriggerRef.value ?? null);

	const { splitIndex, overflowCount, hasOverflow, triggerMaxWidth, recalculate } = useOverflow({
		containerRef: rootEl,
		itemsRef: listEl,
		itemCount: computed(() => options.items()?.length ?? 0),
		itemSelector: '[data-slot="trigger"]',
		triggerRef: triggerEl,
		enabled: computed(() => options.enabled()),
		observeRef: computed(() => rootEl.value?.parentElement ?? null),
		getContainerWidth: getAvailableFlexWidth,
	});

	const itemsWithOverflowUi = computed(() => {
		const items = options.items();
		if (!options.enabled() || !items) return items;

		return items.map((item, index) => ({
			...item,
			...(index >= splitIndex.value ? { disabled: true } : {}),
			ui: {
				...item.ui,
				...(index >= splitIndex.value ? { trigger: `${item.ui?.trigger ?? ''} !absolute !invisible` } : {}),
			},
		})) as T[];
	});

	const overflowItems = computed(() => {
		const items = options.items();
		if (!options.enabled() || !items) return [];
		return items.slice(splitIndex.value) as T[];
	});

	function getItemValue(item: T, indexInOverflow: number): string {
		return (item.value ?? String(splitIndex.value + indexInOverflow)) as string;
	}

	const activeInOverflow = computed(() => {
		if (!hasOverflow.value) return false;
		return overflowItems.value.some((item, i) => getItemValue(item, i) === options.modelValue());
	});

	const activeOverflowItem = computed(() => {
		if (!activeInOverflow.value) return null;
		return overflowItems.value.find((item, i) => getItemValue(item, i) === options.modelValue()) ?? null;
	});

	const overflowDropdownItems = computed<DropdownMenuItem[][]>(() => {
		if (overflowItems.value.length === 0) return [];

		return [
			overflowItems.value.map((item, index) => {
				const value = getItemValue(item, index);
				return {
					label: get(item, options.labelKey()) as string,
					icon: item.icon,
					type: 'checkbox' as const,
					checked: value === options.modelValue(),
					onUpdateChecked: (checked?: boolean) => {
						if (checked) options.onSelect(value);
					},
				};
			}),
		];
	});

	// Re-measure when trigger appears/disappears (guard prevents oscillation at boundary)
	let lastHasOverflow: boolean | null = null;

	watch(hasOverflow, (value) => {
		if (value !== lastHasOverflow) {
			lastHasOverflow = value;
			nextTick(recalculate);
		}
	});

	return {
		itemsWithOverflowUi,
		overflowCount,
		hasOverflow,
		triggerMaxWidth,
		activeInOverflow,
		activeOverflowItem,
		overflowDropdownItems,
	};
}
